'use client';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { SplitText } from 'gsap/all';
import { Text, Container, Icon, useUi } from '@weareconceptstudio/core';

//* Style
import HeroStyle from './style';

//* Helpers
import { animationTitle } from '@/helpers/_animationTitle';
import { markHeroIntroComplete, resetHeroIntroComplete } from '@/helpers/heroAnimation';

//* HOO'C
import { withUIContext } from '@/context';

//* Components
import Button from '@/components/Button';
import FileSection from '@/components/FileSection';
import DeliveryBar from './DeliveryBar';

const Hero = ({ data, loadAnimationPage, loadAnimation, isTouchScreen }) => {
	const { popupIsOpen } = useUi();

	//! Refs
	const allRef = data?.map(() => useRef(null));
	const refSplitText = useRef(null);
	const rightArrowRef = useRef([]);
	const timeoutRef = useRef();
	const wrapperHeroRef = useRef();
	const leftArrowRef = useRef();
	const timelineButton = useRef();
	const contPaddingLR = useRef();

	//! States
	const [activeIndex, setActiveIndex] = useState(0);
	const [firstRender, setFirstRender] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	//! Item qty check
	const oneItem = useMemo(() => {
		return data?.length === 1;
	}, [data]);

	//! Hero Item
	const everyItemHero = useMemo(() => {
		if (data?.length > 0) {
			return data.map((item, i) => {
				const isInternalLink =
					item.additional_info.link.startsWith('/') ||
					item.additional_info.link.includes(process.env.NEXT_PUBLIC_URL);

				return (
					<div
						key={i}
						ref={allRef[i]}
						className={`item-wrapper ${i === 0 ? 'first-wrapper-image' : ''}`}>
						<div className={`wrapper-image-info`}>
							<div className='other-hidden-wrapper' />

							<div className={`image-wrapper`}>
								<FileSection
									priority
									videoLoop
									playVideo
									icon={false}
									onLoad={setIsLoading}
									double={[item.file_main, item.file_mobile]}
								/>
							</div>

							<div className={'info-wrapper'}>
								<Container row>
									{/* <div className='inner-text-link col-6 col-t-7 col-m-12'> */}
									<div className='inner-text-link col-8 col-m-12'>
										<Text
											tag={'h1'}
											className={'title-animation h1 uppercase line-clamp-4 opacity-zero'}>
											{item.title}
										</Text>
										{item.description ? (
											<Text
												className={
													'description-animation p1 uppercase line-clamp-4 opacity-zero'
												}>
												{item.description}
											</Text>
										) : null}

										<div className={`wrapper-button-block`}>
											<Button
												ariaLabel={'karnel'}
												text={'orderNow'}
												external={!isInternalLink}
												className={'wrapper-button'}
												url={item.additional_info.link}
											/>
										</div>
									</div>
								</Container>
							</div>
						</div>
					</div>
				);
			});
		}
	}, [activeIndex]);

	//! Increment-decrement Navigation panel
	function clickNavigation(type) {
		switch (type) {
			case 'increment':
				if (activeIndex === 0) {
					setActiveIndex(data.length - 1);
				} else {
					setActiveIndex(activeIndex - 1);
				}

				break;

			case 'decrement':
				if (activeIndex === data.length - 1) {
					setActiveIndex(0);
				} else {
					setActiveIndex(activeIndex + 1);
				}
				break;

			default:
				break;
		}
	}

	//! Logic Animation
	useEffect(() => {
		//! Plugin
		gsap.registerPlugin(SplitText);

		allRef.map((item, i) => {
			item.current.querySelector('.title-animation').style.opacity = 0;
			const itemDesc = item.current.querySelector('.description-animation');
			if (itemDesc) {
				itemDesc.style.opacity = 0;
			}
			item.current.querySelector('.wrapper-button-block').style.opacity = 0;
			gsap.to(item.current.querySelector('.image-wrapper'), { opacity: 0, duration: 0 });

			item.current.style.zIndex = 0;
		});

		allRef[activeIndex].current.querySelector('.wrapper-button-block').style.opacity = 1;
		allRef[activeIndex].current.style.zIndex = 1;

		if (!firstRender) {
			//! next-item
			allRef[activeIndex].current.style.opacity = 1;
			gsap.to(allRef[activeIndex].current.querySelector('.image-wrapper'), { opacity: 1, duration: 0 });
			gsap.to(allRef[activeIndex].current.querySelector('.wrapper-button-block'), { opacity: 1, duration: 0 });

			if (timelineButton.current) {
				timelineButton.current.kill();
			}
			timelineButton.current = gsap.timeline();
			let widthButton = allRef[activeIndex].current
				?.querySelector('.wrapper-button')
				?.getBoundingClientRect()?.width;

			if (typeof contPaddingLR.current == 'number') {
				widthButton += contPaddingLR.current;
				widthButton = widthButton * -1;
			} else {
				widthButton = '-200%';
			}

			timelineButton.current.fromTo(
				allRef[activeIndex].current.querySelector('.wrapper-button'),
				{ x: widthButton },
				{ x: 0, duration: 0.6, ease: 'power2.out', delay: 0.6 }
			);

			//! Animation Text
			let text = allRef[activeIndex].current.querySelector('.title-animation');
			let textDescription = allRef[activeIndex].current.querySelector('.description-animation');
			animationTitle({ element: text, ref: refSplitText, duration: 0.7, delay: 0.3 });
			if (textDescription) {
				animationTitle({ element: textDescription, ref: refSplitText, duration: 0.7, delay: 0.3 });
			}
		}

		return () => {
			refSplitText.current?.revert();
		};
	}, [activeIndex]);

	//! Load Page Animation
	useEffect(() => {
		if (wrapperHeroRef.current) {
			contPaddingLR.current = parseFloat(
				getComputedStyle(wrapperHeroRef.current?.querySelector('.container'))?.paddingLeft
			);
		}
		if (isLoading) loadAnimationPage();
	}, [isLoading]);

	//! First Load
	useLayoutEffect(() => {
		resetHeroIntroComplete();
	}, []);

	useEffect(() => {
		return () => resetHeroIntroComplete();
	}, []);

	useEffect(() => {
		if (loadAnimation) {
			setTimeout(() => {
				if (firstRender && isLoading) {
					// allRef[activeIndex].current.style.zIndex = data.length + 1;
					allRef[activeIndex].current.style.opacity = 1;
					allRef[activeIndex].current.querySelector('.image-wrapper').style.opacity = 1;
					allRef[activeIndex].current.querySelector('.image-wrapper').classList.add('active', 'first-active');

					gsap.to('.wrapper-button', { x: 0, delay: 0.5, duration: 0.6, ease: 'power2.out' });
					gsap.to('.navigation-wrapper', { opacity: 1, duration: 0.3, ease: 'power2.out', delay: 0.6 });
					gsap.fromTo(
						allRef[activeIndex].current.querySelector('.image-wrapper'),
						{ scale: 0, opacity: 1 },
						{ scale: 1, opacity: 1, duration: 1.2, ease: 'power2.out' }
					).then(() => {
						allRef?.[activeIndex]?.current
							?.querySelector('.image-wrapper')
							.classList.remove('first-active');
						setFirstRender(false);
						markHeroIntroComplete();
					});

					let text = allRef[activeIndex].current.querySelector('.title-animation');
					let descriptionText = allRef[activeIndex].current.querySelector('.description-animation');
					animationTitle({ element: text, ref: refSplitText, duration: 0.7, delay: 0.3 });
					if (descriptionText) {
						animationTitle({ element: descriptionText, ref: refSplitText, duration: 0.7, delay: 0.3 });
					}
				}
			}, 300);
		}
	}, [firstRender, isLoading, loadAnimation]);

	//! Mutation text-inner see image
	useEffect(() => {
		const image = document.querySelector('.image-wrapper');
		const titleTransform = allRef[activeIndex].current.querySelector('.other-hidden-wrapper');
		const handleMutation = (mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
					const transform = image.getBoundingClientRect().x;
					gsap.to(titleTransform, { width: transform, duration: 0 });
				}
			});
		};

		const observer = new MutationObserver(handleMutation);

		observer.observe(image, { attributes: true });

		return () => observer.disconnect();
	}, []);

	// //! Autoplay Photo
	useEffect(() => {
		let button = rightArrowRef.current;

		if (!oneItem) {
			if (!firstRender) {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
				timeoutRef.current = setInterval(() => {
					button?.click();
				}, 10000);
			}

			if (popupIsOpen) {
				clearTimeout(timeoutRef.current);
			}
		}
	}, [firstRender, activeIndex, popupIsOpen, oneItem]);

	//! Logic Draggable
	useEffect(() => {
		if (oneItem) return;

		//! Config
		let isMouseDown = false;
		let directionClick = false;
		let directionTranslate = false;
		let lastX = 0;
		let fixedX = 0;
		let progress = 0;

		//! Progress calculate
		const widthContainer = wrapperHeroRef.current.clientWidth;
		const progressWidth = widthContainer / 2;

		//! Timeline
		const tl = gsap.timeline({ paused: true });
		tl.to(allRef[activeIndex].current, { opacity: 0, ease: 'none' });

		//! start
		function mouseDown(ev) {
			isMouseDown = true;
			lastX = isTouchScreen ? ev.touches?.[0]?.clientX : ev.clientX;
			fixedX = isTouchScreen ? ev.touches?.[0]?.clientX : ev.clientX;

			wrapperHeroRef.current.style.cursor = 'grabbing';
		}
		//! end
		function mouseUp() {
			isMouseDown = false;
			progress = 0;
			let buttonRight = rightArrowRef.current;
			let buttonLeft = leftArrowRef.current;

			wrapperHeroRef.current.style.cursor = 'grab';

			if (directionTranslate) {
				if (directionClick == 'leftClick') {
					buttonRight.click();
				} else if (directionClick == 'rightClick') {
					buttonLeft.click();
				}
			} else {
				gsap.to(allRef[activeIndex].current, { opacity: 1, ease: 'power2.out', duration: 0 });
			}
		}
		//! on drag
		function moveClick(event) {
			if (isMouseDown) {
				const currentX = isTouchScreen ? event.touches[0].clientX : event.clientX;
				const updateCurrentX = currentX - fixedX;
				let currentProgress = Math.abs(updateCurrentX) / progressWidth;
				progress = Number(currentProgress.toFixed(2));

				let rightIndex = activeIndex - 1 >= 0 ? activeIndex - 1 : allRef.length - 1;
				let leftIndex = activeIndex + 1 <= allRef.length - 1 ? activeIndex + 1 : 0;

				if (lastX < fixedX && progress <= 1) {
					directionClick = 'rightClick';
					allRef[rightIndex].current.querySelector('.wrapper-button-block').style.opacity = progress;
					allRef[rightIndex].current.querySelector('.image-wrapper').style.opacity = progress;
					allRef[rightIndex].current.style.opacity = progress;
				} else if (lastX >= fixedX && progress <= 1) {
					directionClick = 'leftClick';
					allRef[leftIndex].current.querySelector('.wrapper-button-block').style.opacity = progress;
					allRef[leftIndex].current.querySelector('.image-wrapper').style.opacity = progress;
					allRef[leftIndex].current.style.opacity = progress;
				}

				//! if drag-distance
				if (progress >= 0.01) {
					directionTranslate = true;
				} else {
					directionTranslate = false;
				}

				if (progress <= 1) {
					gsap.set(tl, { progress: progress });
					lastX = currentX;
				}
			}
		}

		//! Register Mouse Touch
		wrapperHeroRef.current.addEventListener('touchstart', mouseDown, { passive: true });
		wrapperHeroRef.current.addEventListener('touchend', mouseUp, { passive: true });
		wrapperHeroRef.current.addEventListener('touchmove', moveClick, { passive: true });

		wrapperHeroRef.current.addEventListener('mouseup', mouseUp);
		wrapperHeroRef.current.addEventListener('mousedown', mouseDown);
		wrapperHeroRef.current.addEventListener('mousemove', moveClick);

		return () => {
			if (wrapperHeroRef.current) {
				wrapperHeroRef.current.removeEventListener('touchmove', moveClick);
				wrapperHeroRef.current.removeEventListener('touchstart', mouseDown);
				wrapperHeroRef.current.removeEventListener('touchend', mouseUp);

				wrapperHeroRef.current.removeEventListener('mousemove', moveClick);
				wrapperHeroRef.current.removeEventListener('mouseup', mouseUp);
				wrapperHeroRef.current.removeEventListener('mousedown', mouseDown);
			}
		};
	}, [activeIndex, isTouchScreen]);

	return (
		<>
			<HeroStyle
				ref={wrapperHeroRef}
				className={`wrapper-all-hero ${oneItem ? 'one-version' : ''}`}>
				{everyItemHero}

				<DeliveryBar />

				<div className={`navigation-wrapper opacity-zero`}>
					<div className='panel-listed'>
						<Icon
							ref={leftArrowRef}
							name={'left-arrow'}
							className={'cursor-pointer'}
							onClick={clickNavigation.bind(null, 'increment')}
						/>

						<Icon
							ref={rightArrowRef}
							name={'right-arrow'}
							className={'cursor-pointer'}
							onClick={clickNavigation.bind(null, 'decrement')}
						/>
					</div>
				</div>
			</HeroStyle>
		</>
	);
};

export default withUIContext(Hero, ['loadAnimation', 'loadAnimationPage', 'isTouchScreen']);
